import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
function getAttachmentChipContentClassName(presentation) {
    switch (presentation) {
        case 'compact':
            return 'flex min-w-0 items-center gap-1.5 rounded-[inherit]';
        case 'card':
            return 'flex h-full min-w-0 items-center gap-2 rounded-[inherit] px-2';
        case 'media':
            return 'flex size-full items-center justify-center overflow-hidden rounded-[inherit] border border-soft bg-subtle';
        default:
            return assertUnreachableValue(presentation);
    }
}
function getAttachmentChipRootClassName(presentation) {
    const chip = 'flex w-fit max-w-full min-w-0 items-center border border-soft bg-raised shadow-subtle sm:max-w-64';
    switch (presentation) {
        case 'compact':
            return classNames(chip, 'rounded-lg px-1.5 py-1');
        case 'card':
            return classNames(chip, 'h-12 rounded-xl');
        case 'media':
            return 'size-20 shrink-0 rounded-xl after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:transition-colors';
        default:
            return assertUnreachableValue(presentation);
    }
}
function getAttachmentChipAccessoryClassName(presentation) {
    const accessory = 'flex shrink-0 items-center justify-center overflow-hidden';
    switch (presentation) {
        case 'compact':
            return classNames(accessory, 'size-5 rounded-sm text-secondary has-[img,video]:border has-[img,video]:border-soft');
        case 'card':
            return classNames(accessory, 'size-8 rounded-lg bg-soft text-secondary');
        case 'media':
            return classNames(accessory, 'size-full');
        default:
            return assertUnreachableValue(presentation);
    }
}
export function AttachmentChip({ presentation = 'compact', label, leadingAccessory, trailingAccessory, description, isError, isMuted, onRemove, removeLabel, onClick, title, ...props }) {
    const media = presentation === 'media';
    const card = presentation === 'card';
    const replaceIcon = !!onRemove && !media;
    const trailing = onRemove ? (_jsx(AttachmentRemoveButton, { label: removeLabel ?? label, media: media, onClick: onRemove })) : (trailingAccessory);
    const contentClassName = classNames(getAttachmentChipContentClassName(presentation), !media &&
        !replaceIcon &&
        trailing != null &&
        '[@media(hover:none)]:max-w-[calc(100%-26px)] group-hover/attachment:max-w-[calc(100%-26px)] group-focus-within/attachment:max-w-[calc(100%-26px)]');
    const content = (_jsxs(_Fragment, { children: [_jsx("span", { className: classNames(getAttachmentChipAccessoryClassName(presentation), replaceIcon &&
                    '[@media(hover:none)]:invisible group-hover/attachment:invisible group-focus-within/attachment:invisible'), children: leadingAccessory }), _jsxs("div", { className: media ? 'sr-only' : 'min-w-0 text-left', children: [_jsx(TextPrimitive, { variant: "small", weight: "medium", truncate: true, children: label }), description != null && (_jsx("span", { className: isError ? 'block text-xs text-negative-primary' : 'sr-only', children: description }))] })] }));
    return (_jsxs("div", { title: title, className: classNames('group/attachment relative transition-colors', getAttachmentChipRootClassName(presentation), !props.disabled && (media ? 'hover:after:bg-subtle' : 'hover:bg-subtle'), isMuted && 'opacity-70'), children: [onClick ? (_jsx(ButtonPrimitive, { ...props, "aria-label": props['aria-label'] ?? label, onClick: onClick, className: classNames(contentClassName, 'cursor-pointer disabled:cursor-default'), children: content })) : (_jsx("div", { className: contentClassName, children: content })), trailing != null && (_jsx("div", { className: classNames('absolute flex items-center gap-2 [@media(hover:hover)]:opacity-0 group-hover/attachment:opacity-100 group-focus-within/attachment:opacity-100', media
                    ? '-end-1 -top-1'
                    : replaceIcon
                        ? card
                            ? 'start-3.5 top-1/2 -translate-y-1/2'
                            : 'start-1.5 top-1/2 -translate-y-1/2'
                        : 'end-1.5 top-1/2 -translate-y-1/2'), children: trailing }))] }));
}
export function AttachmentRemoveButton({ label, onClick, media = false, }) {
    return (_jsx(ButtonPrimitive, { "aria-label": label, onClick: onClick, className: classNames('flex cursor-pointer items-center justify-center', media
            ? 'rounded-xl border border-inverse bg-inverse p-0.5 text-inverse'
            : 'size-5 rounded-sm text-secondary hover:text-primary'), children: _jsx(Icon, { icon: $icon('x'), size: media ? 'tiny' : 'small' }) }));
}
//# sourceMappingURL=AttachmentChip.js.map